import React, { useState, useEffect } from 'react';
import { Clock, Plus, Edit3, Trash2, Save, X, Copy, Download, Upload, Users, BookOpen, MapPin, Filter, Search, Settings } from 'lucide-react';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  duration: number; // in minutes
}

interface TimetableEntry {
  id: string;
  classId: string;
  className: string;
  section: string;
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Sunday, 1=Monday, etc.
  timeSlotId: string;
  subjectId: string;
  subjectName: string;
  teacherId: string;
  teacherName: string;
  room: string;
  type: 'regular' | 'practical' | 'sports' | 'assembly' | 'break';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Class {
  id: string;
  name: string;
  sections: string[];
}

interface Subject {
  id: string;
  name: string;
  code: string;
}

interface Teacher {
  id: string;
  name: string;
  email: string;
  subjects: string[];
}

const Timetable: React.FC = () => {
  const [timetableEntries, setTimetableEntries] = useState<TimetableEntry[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimetableEntry | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showTimingConfig, setShowTimingConfig] = useState(false);
  const [schoolTiming, setSchoolTiming] = useState({
    startTime: '08:00',
    endTime: '15:00'
  });
  const [periodDuration, setPeriodDuration] = useState(45);
  const [breakDuration, setBreakDuration] = useState(15);
  const [lunchDuration, setLunchDuration] = useState(45);
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);

  const [formData, setFormData] = useState({
    classId: '',
    section: '',
    dayOfWeek: 1 as TimetableEntry['dayOfWeek'],
    timeSlotId: '',
    subjectId: '',
    teacherId: '',
    room: '',
    type: 'regular' as TimetableEntry['type']
  });

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  const entryTypes = [
    { value: 'regular', label: 'Regular Class', color: 'bg-[#16212b] text-[#60a5fa]' },
    { value: 'practical', label: 'Practical/Lab', color: 'bg-[#173322] text-[#4ade80]' },
    { value: 'sports', label: 'Sports/PE', color: 'bg-[#332711] text-[#f0b64c]' },
    { value: 'assembly', label: 'Assembly', color: 'bg-[#241f36] text-[#b794f6]' },
    { value: 'break', label: 'Break', color: 'bg-[#28221a] text-[#b9ad9b]' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const generateTimeSlots = () => {
    const slots: TimeSlot[] = [];
    const startTime = new Date(`2000-01-01T${schoolTiming.startTime}`);
    const endTime = new Date(`2000-01-01T${schoolTiming.endTime}`);

    let currentTime = new Date(startTime);
    let slotId = 1;

    while (currentTime < endTime) {
      const slotStart = new Date(currentTime);
      const slotEnd = new Date(currentTime.getTime() + periodDuration * 60000);

      // Don't exceed school end time
      if (slotEnd > endTime) break;

      slots.push({
        id: slotId.toString(),
        startTime: slotStart.toTimeString().slice(0, 5),
        endTime: slotEnd.toTimeString().slice(0, 5),
        duration: periodDuration
      });

      currentTime = new Date(slotEnd);
      slotId++;

      // Add break after every 2-3 periods (configurable)
      if (slotId === 4 || slotId === 7) { // Morning break and lunch break
        const breakEnd = new Date(currentTime.getTime() + (slotId === 4 ? breakDuration : lunchDuration) * 60000);
        if (breakEnd <= endTime) {
          slots.push({
            id: slotId.toString(),
            startTime: currentTime.toTimeString().slice(0, 5),
            endTime: breakEnd.toTimeString().slice(0, 5),
            duration: slotId === 4 ? breakDuration : lunchDuration
          });
          currentTime = breakEnd;
          slotId++;
        }
      }
    }

    setTimeSlots(slots);
  };

  const saveSchoolTiming = () => {
    generateTimeSlots();
    setShowTimingConfig(false);
  };

  const autoGenerateTimetable = (selectedClasses: string[], daysPerWeek: number, periodsPerDay: number) => {
    const newEntries: TimetableEntry[] = [];
    const usedSlots: Set<string> = new Set();

    selectedClasses.forEach(classId => {
      const classData = classes.find(c => c.id === classId);
      if (!classData) return;

      classData.sections.forEach(section => {
        for (let day = 1; day <= daysPerWeek; day++) {
          const availableTimeSlots = timeSlots.filter(slot =>
            slot.duration === periodDuration // Only regular periods, not breaks
          ).slice(0, periodsPerDay);

          availableTimeSlots.forEach((timeSlot, index) => {
            const slotKey = `${classId}-${section}-${day}-${timeSlot.id}`;
            if (usedSlots.has(slotKey)) return;

            // Randomly assign subjects and teachers
            const availableSubjects = subjects.filter(s => s.code !== 'BREAK');
            const subject = availableSubjects[Math.floor(Math.random() * availableSubjects.length)];
            const availableTeachers = teachers.filter(t => t.subjects.includes(subject.id));
            const teacher = availableTeachers.length > 0
              ? availableTeachers[Math.floor(Math.random() * availableTeachers.length)]
              : teachers[0]; // Fallback to first teacher

            const entry: TimetableEntry = {
              id: `auto-${Date.now()}-${Math.random()}`,
              classId,
              className: classData.name,
              section,
              dayOfWeek: day as TimetableEntry['dayOfWeek'],
              timeSlotId: timeSlot.id,
              subjectId: subject.id,
              subjectName: subject.name,
              teacherId: teacher.id,
              teacherName: teacher.name,
              room: `Room ${Math.floor(Math.random() * 20) + 1}`,
              type: 'regular',
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            newEntries.push(entry);
            usedSlots.add(slotKey);
          });
        }
      });
    });

    setTimetableEntries(prev => [...prev, ...newEntries]);
    setShowAutoGenerate(false);
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Mock data - would be loaded from API
      const mockTimeSlots: TimeSlot[] = [
        { id: '1', startTime: '08:00', endTime: '08:45', duration: 45 },
        { id: '2', startTime: '08:45', endTime: '09:30', duration: 45 },
        { id: '3', startTime: '09:30', endTime: '09:45', duration: 15 }, // Break
        { id: '4', startTime: '09:45', endTime: '10:30', duration: 45 },
        { id: '5', startTime: '10:30', endTime: '11:15', duration: 45 },
        { id: '6', startTime: '11:15', endTime: '12:00', duration: 45 },
        { id: '7', startTime: '12:00', endTime: '12:45', duration: 45 }, // Lunch
        { id: '8', startTime: '12:45', endTime: '13:30', duration: 45 },
        { id: '9', startTime: '13:30', endTime: '14:15', duration: 45 },
        { id: '10', startTime: '14:15', endTime: '15:00', duration: 45 }
      ];

      const mockClasses: Class[] = [
        { id: '1', name: 'Class 1', sections: ['A', 'B'] },
        { id: '2', name: 'Class 2', sections: ['A', 'B', 'C'] },
        { id: '3', name: 'Class 3', sections: ['A', 'B'] },
        { id: '4', name: 'Class 4', sections: ['A', 'B', 'C'] },
        { id: '5', name: 'Class 5', sections: ['A', 'B'] },
        { id: '6', name: 'Class 6', sections: ['A', 'B', 'C'] },
        { id: '7', name: 'Class 7', sections: ['A', 'B'] },
        { id: '8', name: 'Class 8', sections: ['A', 'B', 'C'] },
        { id: '9', name: 'Class 9', sections: ['A', 'B'] },
        { id: '10', name: 'Class 10', sections: ['A', 'B', 'C'] }
      ];

      const mockSubjects: Subject[] = [
        { id: '1', name: 'Mathematics', code: 'MATH' },
        { id: '2', name: 'English', code: 'ENG' },
        { id: '3', name: 'Science', code: 'SCI' },
        { id: '4', name: 'Social Studies', code: 'SST' },
        { id: '5', name: 'Hindi', code: 'HIN' },
        { id: '6', name: 'Computer Science', code: 'CS' },
        { id: '7', name: 'Physical Education', code: 'PE' },
        { id: '8', name: 'Art & Craft', code: 'ART' },
        { id: '9', name: 'Music', code: 'MUS' },
        { id: '10', name: 'Break', code: 'BREAK' }
      ];

      const mockTeachers: Teacher[] = [
        { id: '1', name: 'John Smith', email: 'john.smith@school.com', subjects: ['1', '6'] },
        { id: '2', name: 'Sarah Johnson', email: 'sarah.johnson@school.com', subjects: ['2'] },
        { id: '3', name: 'Michael Brown', email: 'michael.brown@school.com', subjects: ['3'] },
        { id: '4', name: 'Emily Davis', email: 'emily.davis@school.com', subjects: ['4'] },
        { id: '5', name: 'David Wilson', email: 'david.wilson@school.com', subjects: ['5'] },
        { id: '6', name: 'Lisa Anderson', email: 'lisa.anderson@school.com', subjects: ['7'] },
        { id: '7', name: 'Robert Taylor', email: 'robert.taylor@school.com', subjects: ['8'] },
        { id: '8', name: 'Jennifer Martinez', email: 'jennifer.martinez@school.com', subjects: ['9'] }
      ];

      const mockTimetableEntries: TimetableEntry[] = [
        {
          id: '1',
          classId: '1',
          className: 'Class 1',
          section: 'A',
          dayOfWeek: 1,
          timeSlotId: '1',
          subjectId: '1',
          subjectName: 'Mathematics',
          teacherId: '1',
          teacherName: 'John Smith',
          room: 'Room 101',
          type: 'regular',
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        },
        {
          id: '2',
          classId: '1',
          className: 'Class 1',
          section: 'A',
          dayOfWeek: 1,
          timeSlotId: '2',
          subjectId: '2',
          subjectName: 'English',
          teacherId: '2',
          teacherName: 'Sarah Johnson',
          room: 'Room 102',
          type: 'regular',
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        },
        {
          id: '3',
          classId: '1',
          className: 'Class 1',
          section: 'A',
          dayOfWeek: 1,
          timeSlotId: '3',
          subjectId: '10',
          subjectName: 'Break',
          teacherId: '',
          teacherName: '',
          room: '',
          type: 'break',
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        },
        {
          id: '4',
          classId: '1',
          className: 'Class 1',
          section: 'A',
          dayOfWeek: 1,
          timeSlotId: '4',
          subjectId: '3',
          subjectName: 'Science',
          teacherId: '3',
          teacherName: 'Michael Brown',
          room: 'Lab 1',
          type: 'practical',
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        }
      ];

      setTimeSlots(mockTimeSlots);
      setClasses(mockClasses);
      setSubjects(mockSubjects);
      setTeachers(mockTeachers);
      setTimetableEntries(mockTimetableEntries);

      // Set default selection
      if (mockClasses.length > 0) {
        setSelectedClass(mockClasses[0].id);
        if (mockClasses[0].sections.length > 0) {
          setSelectedSection(mockClasses[0].sections[0]);
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async () => {
    try {
      const selectedClassData = classes.find(c => c.id === formData.classId);
      const selectedSubject = subjects.find(s => s.id === formData.subjectId);
      const selectedTeacher = teachers.find(t => t.id === formData.teacherId);

      const newEntry: TimetableEntry = {
        id: Date.now().toString(),
        ...formData,
        className: selectedClassData?.name || '',
        subjectName: selectedSubject?.name || '',
        teacherName: selectedTeacher?.name || '',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      setTimetableEntries([...timetableEntries, newEntry]);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating timetable entry:', error);
    }
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    try {
      const selectedClassData = classes.find(c => c.id === formData.classId);
      const selectedSubject = subjects.find(s => s.id === formData.subjectId);
      const selectedTeacher = teachers.find(t => t.id === formData.teacherId);

      const updatedEntry = {
        ...editingEntry,
        ...formData,
        className: selectedClassData?.name || '',
        subjectName: selectedSubject?.name || '',
        teacherName: selectedTeacher?.name || '',
        updatedAt: new Date().toISOString()
      };

      setTimetableEntries(timetableEntries.map(entry =>
        entry.id === editingEntry.id ? updatedEntry : entry
      ));
      setEditingEntry(null);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error updating timetable entry:', error);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this timetable entry?')) return;

    try {
      setTimetableEntries(timetableEntries.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Error deleting timetable entry:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      classId: '',
      section: '',
      dayOfWeek: 1,
      timeSlotId: '',
      subjectId: '',
      teacherId: '',
      room: '',
      type: 'regular'
    });
  };

  const startEdit = (entry: TimetableEntry) => {
    setEditingEntry(entry);
    setFormData({
      classId: entry.classId,
      section: entry.section,
      dayOfWeek: entry.dayOfWeek,
      timeSlotId: entry.timeSlotId,
      subjectId: entry.subjectId,
      teacherId: entry.teacherId,
      room: entry.room,
      type: entry.type
    });
    setShowCreateForm(true);
  };

  const getFilteredEntries = () => {
    return timetableEntries.filter(entry =>
      entry.classId === selectedClass &&
      entry.section === selectedSection &&
      entry.isActive
    );
  };

  const getEntryForSlot = (dayOfWeek: number, timeSlotId: string) => {
    return getFilteredEntries().find(entry =>
      entry.dayOfWeek === dayOfWeek && entry.timeSlotId === timeSlotId
    );
  };

  const getTypeColor = (type: string) => {
    return entryTypes.find(t => t.value === type)?.color || 'bg-[#28221a] text-[#b9ad9b]';
  };

  const copyTimetable = async () => {
    // Logic to copy timetable from one class/section to another
    alert('Copy timetable functionality would be implemented here');
  };

  const exportTimetable = () => {
    // Logic to export timetable as PDF/Excel
    alert('Export timetable functionality would be implemented here');
  };

  const importTimetable = () => {
    // Logic to import timetable from file
    alert('Import timetable functionality would be implemented here');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#e2916d]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-[#e2916d] rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">Timetable Management</h1>
            <p className="text-[#b9ad9b] mt-1">Manage class schedules and timetables</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-[#211c15] rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-[#211c15] text-[#e2916d] shadow-sm'
                  : 'text-[#b9ad9b] hover:text-[#f3ede2]'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-[#211c15] text-[#e2916d] shadow-sm'
                  : 'text-[#b9ad9b] hover:text-[#f3ede2]'
              }`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => setShowTimingConfig(true)}
            className="flex items-center space-x-2 bg-[#60a5fa] text-white px-4 py-2 rounded-lg hover:bg-[#93c5fd] transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>School Timing</span>
          </button>
          <button
            onClick={() => setShowAutoGenerate(true)}
            className="flex items-center space-x-2 bg-[#4ade80] text-white px-4 py-2 rounded-lg hover:bg-[#6ee7a0] transition-colors"
          >
            <Copy className="w-4 h-4" />
            <span>Auto Generate</span>
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-[#e2916d] text-white px-4 py-2 rounded-lg hover:bg-[#f0a67d] transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Entry</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-[#f3ede2] mb-1">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  const selectedClassData = classes.find(c => c.id === e.target.value);
                  if (selectedClassData && selectedClassData.sections.length > 0) {
                    setSelectedSection(selectedClassData.sections[0]);
                  }
                }}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#f3ede2] mb-1">Section</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                disabled={!selectedClass}
              >
                <option value="">Select Section</option>
                {classes.find(c => c.id === selectedClass)?.sections.map(section => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={copyTimetable}
              className="flex items-center space-x-2 px-3 py-2 text-[#8a7f6e] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </button>
            <button
              onClick={importTimetable}
              className="flex items-center space-x-2 px-3 py-2 text-[#8a7f6e] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Import</span>
            </button>
            <button
              onClick={exportTimetable}
              className="flex items-center space-x-2 px-3 py-2 text-[#8a7f6e] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Timetable Display */}
      {selectedClass && selectedSection ? (
        viewMode === 'grid' ? (
          /* Grid View */
          <div className="bg-[#211c15] rounded-xl border border-[#3a3122] overflow-hidden">
            <div className="p-6 border-b border-[#3a3122]">
              <h2 className="text-lg font-semibold text-[#f3ede2]">
                {classes.find(c => c.id === selectedClass)?.name} - Section {selectedSection}
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#28221a]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-[#f3ede2] w-32">Time</th>
                    {daysOfWeek.map(day => (
                      <th key={day.value} className="px-4 py-3 text-center text-sm font-medium text-[#f3ede2] min-w-[160px]">
                        {day.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3a3122]">
                  {timeSlots.map(slot => (
                    <tr key={slot.id} className="hover:bg-[#28221a]">
                      <td className="px-4 py-4 text-sm font-medium text-[#f3ede2] bg-[#28221a]">
                        <div className="text-center">
                          <div>{slot.startTime}</div>
                          <div className="text-xs text-[#8a7f6e]">to</div>
                          <div>{slot.endTime}</div>
                        </div>
                      </td>
                      {daysOfWeek.map(day => {
                        const entry = getEntryForSlot(day.value, slot.id);
                        return (
                          <td key={`${day.value}-${slot.id}`} className="px-2 py-2 text-center">
                            {entry ? (
                              <div
                                className={`p-3 rounded-lg cursor-pointer transition-colors ${getTypeColor(entry.type)} hover:opacity-80`}
                                onClick={() => startEdit(entry)}
                              >
                                <div className="font-medium text-sm">{entry.subjectName}</div>
                                {entry.teacherName && (
                                  <div className="text-xs opacity-75">{entry.teacherName}</div>
                                )}
                                {entry.room && (
                                  <div className="text-xs opacity-75">{entry.room}</div>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    classId: selectedClass,
                                    section: selectedSection,
                                    dayOfWeek: day.value as TimetableEntry['dayOfWeek'],
                                    timeSlotId: slot.id
                                  });
                                  setShowCreateForm(true);
                                }}
                                className="w-full h-16 border-2 border-dashed border-[#3a3122] rounded-lg text-[#8a7f6e] hover:border-[#e2916d] hover:text-[#e2916d] transition-colors flex items-center justify-center"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
            <div className="p-6 border-b border-[#3a3122]">
              <h2 className="text-lg font-semibold text-[#f3ede2]">
                {classes.find(c => c.id === selectedClass)?.name} - Section {selectedSection} Schedule
                <span className="ml-2 text-sm font-normal text-[#b9ad9b]">
                  ({getFilteredEntries().length} entries)
                </span>
              </h2>
            </div>

            <div className="divide-y divide-[#3a3122]">
              {getFilteredEntries().length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#f3ede2] mb-2">No timetable entries</h3>
                  <p className="text-[#8a7f6e]">Get started by adding your first timetable entry.</p>
                </div>
              ) : (
                getFilteredEntries()
                  .sort((a, b) => a.dayOfWeek - b.dayOfWeek || timeSlots.findIndex(t => t.id === a.timeSlotId) - timeSlots.findIndex(t => t.id === b.timeSlotId))
                  .map((entry) => {
                    const timeSlot = timeSlots.find(t => t.id === entry.timeSlotId);
                    const dayName = daysOfWeek.find(d => d.value === entry.dayOfWeek)?.label;

                    return (
                      <div key={entry.id} className="p-6 hover:bg-[#28221a] transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-medium text-[#f3ede2]">{entry.subjectName}</h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(entry.type)}`}>
                                {entryTypes.find(t => t.value === entry.type)?.label}
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#8a7f6e]">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{dayName} - {timeSlot?.startTime} to {timeSlot?.endTime}</span>
                              </div>

                              {entry.teacherName && (
                                <div className="flex items-center space-x-1">
                                  <Users className="w-4 h-4" />
                                  <span>{entry.teacherName}</span>
                                </div>
                              )}

                              {entry.room && (
                                <div className="flex items-center space-x-1">
                                  <MapPin className="w-4 h-4" />
                                  <span>{entry.room}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => startEdit(entry)}
                              className="p-2 text-[#8a7f6e] hover:text-[#e2916d] hover:bg-[#332217] rounded-lg transition-colors"
                              title="Edit entry"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                              title="Delete entry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        )
      ) : (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-8 text-center">
          <Clock className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#f3ede2] mb-2">Select a class and section</h3>
          <p className="text-[#8a7f6e]">Choose a class and section to view or manage the timetable.</p>
        </div>
      )}

      {/* Create/Edit Entry Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-always-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#211c15] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f3ede2]">
                {editingEntry ? 'Edit Timetable Entry' : 'Add Timetable Entry'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingEntry(null);
                  resetForm();
                }}
                className="p-2 text-[#8a7f6e] hover:text-[#f3ede2] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              editingEntry ? handleUpdateEntry() : handleCreateEntry();
            }} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Class *
                  </label>
                  <select
                    required
                    value={formData.classId}
                    onChange={(e) => setFormData({...formData, classId: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    <option value="">Select Class</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Section *
                  </label>
                  <select
                    required
                    value={formData.section}
                    onChange={(e) => setFormData({...formData, section: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    disabled={!formData.classId}
                  >
                    <option value="">Select Section</option>
                    {classes.find(c => c.id === formData.classId)?.sections.map(section => (
                      <option key={section} value={section}>{section}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Day of Week *
                  </label>
                  <select
                    required
                    value={formData.dayOfWeek}
                    onChange={(e) => setFormData({...formData, dayOfWeek: parseInt(e.target.value) as TimetableEntry['dayOfWeek']})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    {daysOfWeek.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Time Slot *
                  </label>
                  <select
                    required
                    value={formData.timeSlotId}
                    onChange={(e) => setFormData({...formData, timeSlotId: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    <option value="">Select Time Slot</option>
                    {timeSlots.map(slot => (
                      <option key={slot.id} value={slot.id}>
                        {slot.startTime} - {slot.endTime} ({slot.duration} min)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Subject *
                  </label>
                  <select
                    required
                    value={formData.subjectId}
                    onChange={(e) => setFormData({...formData, subjectId: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Teacher
                  </label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => setFormData({...formData, teacherId: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Room/Location
                  </label>
                  <input
                    type="text"
                    value={formData.room}
                    onChange={(e) => setFormData({...formData, room: e.target.value})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    placeholder="e.g., Room 101, Lab A, Playground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Type *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value as TimetableEntry['type']})}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  >
                    {entryTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingEntry(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.classId || !formData.section || !formData.timeSlotId || !formData.subjectId}
                  className="flex items-center space-x-2 bg-[#e2916d] text-white px-4 py-2 rounded-lg hover:bg-[#f0a67d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingEntry ? 'Update Entry' : 'Create Entry'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* School Timing Configuration Modal */}
      {showTimingConfig && (
        <div className="fixed inset-0 bg-always-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#211c15] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f3ede2]">School Timing Configuration</h2>
              <button
                onClick={() => setShowTimingConfig(false)}
                className="text-[#8a7f6e] hover:text-[#b9ad9b]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              saveSchoolTiming();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                  School Start Time
                </label>
                <input
                  type="time"
                  value={schoolTiming.startTime}
                  onChange={(e) => setSchoolTiming(prev => ({ ...prev, startTime: e.target.value }))}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                  School End Time
                </label>
                <input
                  type="time"
                  value={schoolTiming.endTime}
                  onChange={(e) => setSchoolTiming(prev => ({ ...prev, endTime: e.target.value }))}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Period Duration (min)
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="90"
                    value={periodDuration}
                    onChange={(e) => setPeriodDuration(Number(e.target.value))}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Break Duration (min)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="30"
                    value={breakDuration}
                    onChange={(e) => setBreakDuration(Number(e.target.value))}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Lunch Duration (min)
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="60"
                    value={lunchDuration}
                    onChange={(e) => setLunchDuration(Number(e.target.value))}
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    required
                  />
                </div>
              </div>

              <div className="bg-[#28221a] p-4 rounded-lg">
                <h3 className="font-medium text-[#f3ede2] mb-2">Current School Timing</h3>
                <p className="text-[#b9ad9b] text-sm">
                  <strong>Start:</strong> {schoolTiming.startTime}
                </p>
                <p className="text-[#b9ad9b] text-sm">
                  <strong>End:</strong> {schoolTiming.endTime}
                </p>
                <p className="text-[#b9ad9b] text-sm mt-2">
                  <strong>Total Duration:</strong> {
                    (() => {
                      const start = new Date(`2000-01-01T${schoolTiming.startTime}`);
                      const end = new Date(`2000-01-01T${schoolTiming.endTime}`);
                      const diffMs = end.getTime() - start.getTime();
                      const hours = Math.floor(diffMs / (1000 * 60 * 60));
                      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                      return `${hours}h ${minutes}m`;
                    })()
                  }
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTimingConfig(false)}
                  className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] transition-colors"
                >
                  Save Timing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auto Generate Timetable Modal */}
      {showAutoGenerate && (
        <div className="fixed inset-0 bg-always-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#211c15] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f3ede2]">Auto Generate Timetable</h2>
              <button
                onClick={() => setShowAutoGenerate(false)}
                className="text-[#8a7f6e] hover:text-[#b9ad9b]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const selectedClasses = Array.from(formData.getAll('classes')) as string[];
              const daysPerWeek = Number(formData.get('daysPerWeek'));
              const periodsPerDay = Number(formData.get('periodsPerDay'));

              if (selectedClasses.length === 0) {
                window.alert('Please select at least one class');
                return;
              }

              autoGenerateTimetable(selectedClasses, daysPerWeek, periodsPerDay);
            }} className="space-y-6">

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-3">
                  Select Classes to Generate Timetable
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {classes.map(classItem => (
                    <label key={classItem.id} className="flex items-center space-x-2 p-3 border border-[#3a3122] rounded-lg hover:bg-[#28221a] cursor-pointer">
                      <input
                        type="checkbox"
                        name="classes"
                        value={classItem.id}
                        className="text-[#e2916d] focus:ring-[#e2916d]"
                      />
                      <span className="text-sm text-[#f3ede2]">{classItem.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Days per Week
                  </label>
                  <select
                    name="daysPerWeek"
                    defaultValue="5"
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    required
                  >
                    <option value="5">5 Days (Mon-Fri)</option>
                    <option value="6">6 Days (Mon-Sat)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#f3ede2] mb-2">
                    Periods per Day
                  </label>
                  <select
                    name="periodsPerDay"
                    defaultValue="6"
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                    required
                  >
                    <option value="4">4 Periods</option>
                    <option value="5">5 Periods</option>
                    <option value="6">6 Periods</option>
                    <option value="7">7 Periods</option>
                    <option value="8">8 Periods</option>
                  </select>
                </div>
              </div>

              <div className="bg-[#332711] border border-[#4a3a19] rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-[#332711] rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-[#f0b64c] text-sm font-medium">!</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-[#f9daa3] mb-1">Auto Generation Notes</h4>
                    <ul className="text-sm text-[#f5c876] space-y-1">
                      <li>• Subjects and teachers will be randomly assigned</li>
                      <li>• Room numbers will be auto-generated</li>
                      <li>• Breaks and lunch periods are excluded from generation</li>
                      <li>• You can edit individual entries after generation</li>
                      <li>• This will add to existing timetable entries</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAutoGenerate(false)}
                  className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#4ade80] text-white rounded-lg hover:bg-[#6ee7a0] transition-colors"
                >
                  Generate Timetable
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timetable;